import { useEffect } from 'react'

interface DocumentMeta {
  title?: string
  titleAbsolute?: string
  description?: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'profile'
  canonical?: string
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>
  /** robots meta value. Use 'noindex' for 404, search-result, and admin pages. */
  robots?: string
}

const BRAND = 'Chinedu Koggu'
const JSONLD_ID = 'page-jsonld'

function setMeta(name: string, content: string): () => void {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  const created = !el
  if (!el) {
    el = document.createElement('meta')
    el.name = name
    document.head.appendChild(el)
  }
  const prev = el.content
  el.content = content
  return () => {
    if (created) el.remove()
    else el!.content = prev
  }
}

function setOg(property: string, content: string): () => void {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
  const created = !el
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  const prev = el.content
  el.content = content
  return () => {
    if (created) el.remove()
    else el!.content = prev
  }
}

function setLink(rel: string, href: string): () => void {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  const created = !el
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    document.head.appendChild(el)
  }
  const prev = el.href
  el.href = href
  return () => {
    if (created) el.remove()
    else el!.href = prev
  }
}

function setJsonLd(data: Record<string, unknown> | Array<Record<string, unknown>>): () => void {
  const existing = document.getElementById(JSONLD_ID)
  if (existing) existing.remove()
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = JSONLD_ID
  script.text = JSON.stringify(data)
  document.head.appendChild(script)
  return () => {
    script.remove()
  }
}

export function useDocumentMeta({
  title,
  titleAbsolute,
  description,
  ogImage,
  ogType = 'website',
  canonical,
  jsonLd,
  robots,
}: DocumentMeta): void {
  useEffect(() => {
    const prevTitle = document.title
    const fullTitle = titleAbsolute || (title ? `${title} — ${BRAND}` : BRAND)
    document.title = fullTitle

    const cleanups: Array<() => void> = []

    if (robots) {
      cleanups.push(setMeta('robots', robots))
    }

    if (description) {
      cleanups.push(setMeta('description', description))
      cleanups.push(setOg('og:description', description))
      cleanups.push(setMeta('twitter:description', description))
    }

    cleanups.push(setOg('og:title', fullTitle))
    cleanups.push(setOg('og:type', ogType))
    cleanups.push(setMeta('twitter:title', fullTitle))
    cleanups.push(setMeta('twitter:card', 'summary_large_image'))

    if (ogImage) {
      cleanups.push(setOg('og:image', ogImage))
      cleanups.push(setMeta('twitter:image', ogImage))
    }

    const url = canonical ?? (typeof window !== 'undefined' ? window.location.href : '')
    if (url) {
      cleanups.push(setOg('og:url', url))
      cleanups.push(setLink('canonical', url))
    }

    if (jsonLd) {
      cleanups.push(setJsonLd(jsonLd))
    }

    return () => {
      document.title = prevTitle
      cleanups.forEach((fn) => fn())
    }
  }, [title, titleAbsolute, description, ogImage, ogType, canonical, jsonLd, robots])
}
